import React, { useState } from 'react';
import { BarChart2, PieChart, Check } from 'lucide-react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, Pie } from 'recharts';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент визуализации бизнес-метрик и рентабельности
 */
const BusinessMetricsChart = ({ 
  results, 
  historicalData, 
  selectedMetrics, 
  setSelectedMetrics,
  industryType,
  industryTypes
}) => {
  const [chartType, setChartType] = useState('bar'); // 'bar', 'pie', 'line'
  
  if (!results) return null;

  // Получаем данные по отрасли для сравнения
  const industry = industryTypes.find(ind => ind.id === industryType) || {
    name: 'Не указана',
    avgGrossMargin: 0,
    avgNetMargin: 0,
    avgROI: 0
  };

  // Определяем метрики для отображения
  const metricsOptions = [
    { id: 'grossProfitMargin', name: 'Валовая маржа', color: '#10b981' },
    { id: 'operatingMargin', name: 'Операционная маржа', color: '#0ea5e9' },
    { id: 'netProfitMargin', name: 'Чистая маржа', color: '#8b5cf6' },
    { id: 'roi', name: 'ROI', color: '#ec4899' },
    { id: 'roa', name: 'ROA', color: '#f97316' },
    { id: 'roe', name: 'ROE', color: '#eab308' },
    { id: 'ebitdaMargin', name: 'EBITDA маржа', color: '#14b8a6' },
  ];

  // Обработчик переключения метрик
  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => {
      // Если метрика уже выбрана, удаляем ее
      if (prev.includes(metricId)) {
        // Не удаляем, если это последняя выбранная метрика
        if (prev.length <= 1) return prev;
        return prev.filter(id => id !== metricId);
      }
      // Добавляем метрику, если она еще не выбрана
      return [...prev, metricId];
    });
  };

  // Подготовка данных для разных типов графиков
  // 1. Данные для столбчатой диаграммы
  const prepareBarChartData = () => {
    // Фильтруем только выбранные метрики
    const filteredMetrics = metricsOptions.filter(metric => 
      selectedMetrics.includes(metric.id)
    );
    
    return [
      // Данные текущего бизнеса
      {
        name: 'Ваш бизнес',
        ...filteredMetrics.reduce((acc, metric) => ({
          ...acc,
          [metric.id]: results[metric.id]
        }), {})
      },
      // Средние показатели по отрасли
      {
        name: 'Среднее по отрасли',
        grossProfitMargin: industry.avgGrossMargin,
        netProfitMargin: industry.avgNetMargin,
        roi: industry.avgROI,
        // Дополнительные метрики могут быть недоступны для среднего по отрасли
        operatingMargin: undefined,
        roa: undefined,
        roe: undefined,
        ebitdaMargin: undefined
      }
    ];
  };

  // 2. Данные для круговой диаграммы
  const preparePieChartData = () => {
    return [
      { name: 'Себестоимость', value: results.costOfGoods, color: '#f43f5e' },
      { name: 'Операционные расходы', value: results.operatingExpenses, color: '#fb923c' },
      { name: 'Налоги', value: results.earningsBeforeTax * results.taxRate, color: '#a1a1aa' },
      { name: 'Чистая прибыль', value: Math.max(0, results.netProfit), color: '#10b981' }
    ];
  };
  
  // 3. Данные для линейного графика (историческая тенденция)
  const prepareLineChartData = () => {
    // Если нет исторических данных, используем только текущие результаты
    if (historicalData.length === 0) {
      return [{
        name: 'Текущий период',
        ...metricsOptions.reduce((acc, metric) => ({
          ...acc,
          [metric.id]: results[metric.id]
        }), {})
      }];
    }
    
    // Сортируем исторические данные по дате (от старых к новым)
    const sortedData = [...historicalData].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    
    // Добавляем текущие результаты в конец, если они не совпадают с последними историческими
    const formattedData = sortedData.map((item, index) => ({
      name: `Период ${index + 1}`,
      ...metricsOptions.reduce((acc, metric) => ({
        ...acc,
        [metric.id]: item[metric.id]
      }), {})
    }));
    
    // Проверяем, совпадают ли текущие результаты с последними историческими
    const lastHistorical = sortedData[sortedData.length - 1];
    const isCurrent = lastHistorical &&
      new Date(lastHistorical.date).toDateString() === new Date(results.calculationDate).toDateString();
    
    if (!isCurrent) {
      formattedData.push({
        name: `Текущий период`,
        ...metricsOptions.reduce((acc, metric) => ({
          ...acc,
          [metric.id]: results[metric.id]
        }), {})
      });
    }
    
    return formattedData;
  };

  // Получаем данные в зависимости от выбранного типа графика
  const chartData = chartType === 'bar' 
    ? prepareBarChartData() 
    : chartType === 'pie' 
      ? preparePieChartData()
      : prepareLineChartData();

  // Пользовательский компонент для подсказок на графике
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    
    return (
      <div className="bg-white dark:bg-gray-800 p-2 border border-gray-200 dark:border-gray-700 rounded shadow-sm text-xs">
        <p className="font-medium text-gray-900 dark:text-white mb-1">{label}</p>
        {payload.map((entry, index) => {
          const metric = metricsOptions.find(m => m.id === entry.dataKey);
          return (
            <div key={`item-${index}`} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }}></div>
              <span className="text-gray-700 dark:text-gray-300">
                {metric ? metric.name : entry.name}: {formatNumber(entry.value, 2)}
                {entry.dataKey.includes('Margin') || entry.dataKey === 'roi' || entry.dataKey === 'roa' || entry.dataKey === 'roe' ? '%' : ''}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-emerald-500" />
          Визуализация показателей рентабельности
        </h3>
        
        {/* Селектор типа графика */}
        <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-md p-1">
          <button
            onClick={() => setChartType('bar')}
            className={`py-1 px-3 text-xs font-medium rounded-md transition ${
              chartType === 'bar'
                ? 'bg-white dark:bg-gray-600 text-emerald-600 dark:text-emerald-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <BarChart2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setChartType('pie')}
            className={`py-1 px-3 text-xs font-medium rounded-md transition ${
              chartType === 'pie'
                ? 'bg-white dark:bg-gray-600 text-emerald-600 dark:text-emerald-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <PieChart className="w-4 h-4" />
          </button>
          <button
            onClick={() => setChartType('line')}
            className={`py-1 px-3 text-xs font-medium rounded-md transition ${
              chartType === 'line'
                ? 'bg-white dark:bg-gray-600 text-emerald-600 dark:text-emerald-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18"/>
              <path d="M18 9l-3 3 -3 -5 -3 2 -3 3"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Переключатели метрик (только для bar и line графиков) */}
      {chartType !== 'pie' && (
        <div className="mb-4 flex flex-wrap gap-2">
          {metricsOptions.map(metric => (
            <button
              key={metric.id}
              onClick={() => toggleMetric(metric.id)}
              className={`py-1 px-2 text-xs rounded-md border transition ${
                selectedMetrics.includes(metric.id)
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center">
                {selectedMetrics.includes(metric.id) && (
                  <Check className="w-3 h-3 mr-1" />
                )}
                <span>{metric.name}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Контейнер графика */}
      <div className="mt-4 h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'bar' ? (
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
              <XAxis dataKey="name" />
              <YAxis unit="%" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {selectedMetrics.map(metricId => {
                const metric = metricsOptions.find(m => m.id === metricId);
                return (
                  <Bar
                    key={metricId}
                    dataKey={metricId}
                    name={metric ? metric.name : metricId}
                    fill={metric ? metric.color : '#8884d8'}
                  />
                );
              })}
            </BarChart>
          ) : chartType === 'pie' ? (
            <PieChart>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          ) : (
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
              <XAxis dataKey="name" />
              <YAxis unit="%" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {selectedMetrics.map(metricId => {
                const metric = metricsOptions.find(m => m.id === metricId);
                return (
                  <Line
                    key={metricId}
                    type="monotone"
                    dataKey={metricId}
                    name={metric ? metric.name : metricId}
                    stroke={metric ? metric.color : '#8884d8'}
                    activeDot={{ r: 8 }}
                  />
                );
              })}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Пояснение к графику */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {chartType === 'bar' 
            ? `График показывает ключевые показатели рентабельности в сравнении со средними значениями по отрасли "${industry.name}". Выберите интересующие вас метрики для более детального анализа.`
            : chartType === 'pie'
              ? 'График показывает структуру распределения выручки на себестоимость, операционные расходы, налоги и чистую прибыль.'
              : historicalData.length > 0
                ? 'График отображает динамику изменения показателей рентабельности за несколько периодов. Выберите интересующие вас метрики для более детального анализа.'
                : 'График показывает текущие показатели рентабельности. По мере сохранения результатов анализа здесь будет отображаться динамика их изменения.'}
        </p>
      </div>
    </div>
  );
};

export default BusinessMetricsChart;